import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../../../Context/ThemeContext';
import { AdministratorHandler } from '../AdministratorHandler';
import { SlimAdministrator } from '../../../../Core/Api/Api';

const AdministratorDetailView: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const { theme, isMobile } = useTheme();

    const [admin, setAdmin] = useState<SlimAdministrator | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!userId) return;
        const fetchAdmin = async () => {
            setLoading(true);
            setError(null);
            try {
                const handler = new AdministratorHandler();
                const response = await handler.getAdminByUserId(userId);
                setAdmin(response.administrator);
            } catch (err) {
                console.error('Error fetching administrator:', err);
                setError('Failed to load administrator details.');
            } finally {
                setLoading(false);
            }
        };
        fetchAdmin();
    }, [userId]);

    const getStatusColor = (status: string | undefined) => {
        switch (status) {
            case 'Active': return { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' };
            case 'Disabled': return { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' };
            case 'Locked': return { bg: 'rgba(251, 146, 60, 0.2)', color: '#fb923c' };
            default: return { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' };
        }
    };

    const handleDelete = async () => {
        if (!userId) return;
        setDeleting(true);
        try {
            const handler = new AdministratorHandler();
            await handler.deleteAdminUser({ userId });
            navigate('/Admin/Users/Administrators');
        } catch (err) {
            console.error('Error deleting administrator:', err);
            setError('Failed to delete administrator.');
            setShowDeleteConfirm(false);
        } finally {
            setDeleting(false);
        }
    };

    const styles: Record<string, React.CSSProperties> = {
        container: { padding: 0 },
        header: { marginBottom: '24px' },
        title: { fontSize: isMobile ? '24px' : '28px', fontWeight: 700, color: theme.titleColor, marginBottom: '8px' },
        subtitle: { fontSize: '14px', color: theme.textSecondary, marginBottom: '16px' },
        headerActions: { display: 'flex', gap: '12px', flexWrap: 'wrap' as const },
        backButton: {
            padding: '8px 16px', fontSize: '13px', fontWeight: 600,
            background: 'transparent', color: theme.text,
            border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', cursor: 'pointer',
        },
        editButton: {
            padding: '8px 16px', fontSize: '13px', fontWeight: 600,
            background: '#3b82f6', color: '#fff',
            border: 'none', borderRadius: '6px', cursor: 'pointer',
        },
        deleteButton: {
            padding: '8px 16px', fontSize: '13px', fontWeight: 600,
            background: '#ef4444', color: '#fff',
            border: 'none', borderRadius: '6px', cursor: 'pointer',
        },
        overlay: {
            position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        },
        confirmModal: {
            background: theme.card, borderRadius: '12px', padding: '24px',
            maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        },
        confirmTitle: { fontSize: '18px', fontWeight: 700, color: theme.text, marginBottom: '12px' },
        confirmText: { fontSize: '14px', color: theme.textSecondary, marginBottom: '20px', lineHeight: 1.5 },
        confirmActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px' },
        confirmCancel: {
            padding: '8px 16px', fontSize: '14px', fontWeight: 500, color: theme.text,
            background: 'transparent', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', cursor: 'pointer',
        },
        confirmDelete: {
            padding: '8px 16px', fontSize: '14px', fontWeight: 600, color: '#fff',
            background: '#ef4444', border: 'none', borderRadius: '6px', cursor: 'pointer',
        },
        card: {
            background: theme.card, borderRadius: '8px',
            padding: isMobile ? '16px' : '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '20px',
        },
        cardTitle: { fontSize: '16px', fontWeight: 700, color: theme.text, marginBottom: '16px' },
        grid: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '20px',
        },
        fieldLabel: { fontSize: '13px', color: theme.textSecondary, marginBottom: '4px' },
        fieldValue: { fontSize: '15px', fontWeight: 500, color: theme.text },
        badge: {
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '4px 12px', borderRadius: '12px',
            fontSize: '13px', fontWeight: 600,
        },
        roleBadge: {
            display: 'inline-block', padding: '4px 10px', borderRadius: '12px',
            fontSize: '12px', fontWeight: 600,
            background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa',
            marginRight: '6px', marginBottom: '6px',
        },
        avatar: {
            width: '64px', height: '64px', borderRadius: '50%',
            background: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '28px', fontWeight: 700, marginBottom: '16px',
        },
        spinner: {
            width: '32px', height: '32px',
            border: '3px solid rgba(59, 130, 246, 0.3)',
            borderTop: '3px solid #3b82f6', borderRadius: '50%',
            animation: 'spin 1s linear infinite',
        },
        errorBanner: {
            padding: '12px 16px', borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#ef4444', fontSize: '14px', fontWeight: 500, marginBottom: '16px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        },
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 0', gap: '16px' }}>
                    <div style={styles.spinner} />
                    <span style={{ color: theme.textSecondary }}>Loading administrator details...</span>
                </div>
            </div>
        );
    }

    if (error || !admin) {
        return (
            <div style={styles.container}>
                <div style={styles.errorBanner}>
                    <span>{error || 'Administrator not found.'}</span>
                    <button onClick={() => navigate(-1)} style={styles.backButton}>Go Back</button>
                </div>
            </div>
        );
    }

    const statusColors = getStatusColor(String(admin.status));

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Administrator Details</h1>
                <p style={styles.subtitle}>Viewing details for {admin.username}</p>
                <div style={styles.headerActions}>
                    <button style={styles.backButton} onClick={() => navigate('/Admin/Users/Administrators')}>
                        Back to List
                    </button>
                    <button style={styles.editButton} onClick={() => navigate(`/Admin/Users/Administrators/${userId}/Edit`)}>
                        Edit Administrator
                    </button>
                    <button style={styles.deleteButton} onClick={() => setShowDeleteConfirm(true)}>
                        Delete Administrator
                    </button>
                </div>
            </div>

            {/* Profile Card */}
            <div style={styles.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
                    <div style={styles.avatar}>
                        {(admin.username || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: theme.text }}>{admin.username}</div>
                        <div style={{ fontSize: '14px', color: theme.textSecondary }}>{admin.email}</div>
                        <div style={{ marginTop: '8px' }}>
                            <span style={{ ...styles.badge, background: statusColors.bg, color: statusColors.color }}>
                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: statusColors.color }} />
                                {String(admin.status)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Details Card */}
            <div style={styles.card}>
                <div style={styles.cardTitle}>Personal Information</div>
                <div style={styles.grid}>
                    <div>
                        <div style={styles.fieldLabel}>First Name</div>
                        <div style={styles.fieldValue}>{admin.firstName || '-'}</div>
                    </div>
                    <div>
                        <div style={styles.fieldLabel}>Last Name</div>
                        <div style={styles.fieldValue}>{admin.lastName || '-'}</div>
                    </div>
                    <div>
                        <div style={styles.fieldLabel}>Email</div>
                        <div style={styles.fieldValue}>{admin.email || '-'}</div>
                    </div>
                    <div>
                        <div style={styles.fieldLabel}>Email Confirmed</div>
                        <div style={styles.fieldValue}>{admin.emailConfirmed ? 'Yes' : 'No'}</div>
                    </div>
                    <div>
                        <div style={styles.fieldLabel}>Currently Logged In</div>
                        <div style={styles.fieldValue}>{admin.loggedIn ? 'Yes' : 'No'}</div>
                    </div>
                </div>
            </div>

            {/* Roles Card */}
            <div style={styles.card}>
                <div style={styles.cardTitle}>Roles & Permissions</div>
                {admin.roles && admin.roles.length > 0 ? (
                    <div>
                        {admin.roles.map((role, idx) => (
                            <span key={idx} style={styles.roleBadge}>{role}</span>
                        ))}
                    </div>
                ) : (
                    <span style={{ color: theme.textSecondary, fontStyle: 'italic' }}>No roles assigned</span>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div style={styles.overlay} onClick={() => setShowDeleteConfirm(false)}>
                    <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.confirmTitle}>Delete Administrator</div>
                        <div style={styles.confirmText}>
                            Are you sure you want to delete <strong>{admin.username}</strong>? This will remove all their roles and disable their admin access. This action cannot be undone.
                        </div>
                        <div style={styles.confirmActions}>
                            <button style={styles.confirmCancel} onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                                Cancel
                            </button>
                            <button style={styles.confirmDelete} onClick={handleDelete} disabled={deleting}>
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdministratorDetailView;
